import * as Api from '@ViewModels';
import * as React from 'react';
import { SalesLeadAIContext } from '../../../extViewmodels/sdk/models/AI';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useAIConversationEnrichCreationContextMutation } from '../../../queries/AI/useAIConversationEnrichCreationContextMutation';
import { getConversationChatMessageQueryKey } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { LIVE_SUGGESTION_PROMPT_HEADER } from '../../constants/coachingContract';
import { TranscriptEntry } from '../types';
import {
	AICoachingMessage,
	TYSON_ASSISTANT_NAME,
	TYSON_END_OF_CALL_SUMMARY_PROMPT,
	TYSON_GAME_PLAN_KICKOFF_PROMPT,
	TysonCoachingContextData,
} from '../types/coaching';

interface UseTysonRealtimeCoachingReturn {
	coachingMessages: AICoachingMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isAIResponding: boolean;
	createConversation: (data?: TysonCoachingContextData) => Promise<void>;
	enrichCreationContext: (params: { phoneCallId?: string; leadServedId?: string; source?: string }) => void;
	sendTranscriptEntry: (entry: TranscriptEntry) => void;
	sendTranscriptBatchImmediately: (entries: TranscriptEntry[]) => void;
	flushPendingEntries: () => TranscriptEntry[];
	sendEndOfCallSummary: () => void;
	sendEndOfCallSummaryWithTranscript: (formattedTranscript: string) => void;
	sendDirectQuestion: (question: string) => void;
	sendNextContactStartedAction: (contactName: string) => void;
	sendPracticeCallStartedAction: (contactName: string) => void;
	resetCoaching: () => void;
}

export const useTysonRealtimeCoaching = (contextData: TysonCoachingContextData): UseTysonRealtimeCoachingReturn => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useTysonRealtimeCoaching');
	const userSession = useUserSession();

	const [conversationId, setConversationId] = React.useState<string>(null);
	const conversationIdRef = React.useRef<string>(null);
	const [coachingMessages, setCoachingMessages] = React.useState<AICoachingMessage[]>([]);
	// Pending entries are accumulated by sendTranscriptEntry and flushed by flushPendingEntries
	const [pendingTranscriptEntries, setPendingTranscriptEntries] = React.useState<TranscriptEntry[]>([]);

	// Keep ref in sync with state
	React.useEffect(() => {
		conversationIdRef.current = conversationId;
	}, [conversationId]);

	// Enrich creation context (e.g., add phoneCallId after a real call starts)
	const enrichCreationContextMutation = useAIConversationEnrichCreationContextMutation({
		onError: error => {
			logger.logApiError('enrichCreationContextMutation', error);
		},
	});

	// Create AI conversation
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: conversation => {
			setConversationId(conversation.id);

			// autoStart alone only makes the agent greet, so also send an explicit Game Plan ask or the Game Plan card never loads.
			const contexts: Api.AIContext[] = [];
			if (contextData?.companyId) {
				contexts.push({
					_type: 'SalesLeadAIContext',
					companyId: contextData.companyId,
				} as SalesLeadAIContext);
			}

			const chatMessages: Api.AIChatMessage[] = [
				{
					role: Api.AIChatRole.User,
					text: TYSON_GAME_PLAN_KICKOFF_PROMPT,
					id: null,
				},
			];

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: conversation.id,
				chatRequest: {
					...(contexts.length > 0 && { contexts }),
					autoStart: true,
					chatMessages,
				} as Api.AIMessageRequest,
			});
		},
	});

	// Stream AI responses. `chatRequest` is the base `AIChatRequest` so the
	// same mutation handles user-message streams, kickoff `autoStart` streams,
	// and structured `AIActionRequest` payloads (e.g. next-contact / practice-
	// call-started actions). The HTTP endpoint dispatches on body shape.
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			convoId,
			chatRequest,
		}: {
			assistantName: string;
			convoId: string;
			chatRequest: Api.AIChatRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;
			const isDelta = data?.isDelta;

			if (messageText) {
				setCoachingMessages(prev => {
					const lastMessage = prev[prev.length - 1];

					if (lastMessage && lastMessage.isStreaming) {
						// Update existing streaming message - just concatenate directly
						// LLM tokens include proper spacing
						const updatedText = isDelta ? lastMessage.text + messageText : messageText;

						return [
							...prev.slice(0, -1),
							{
								...lastMessage,
								text: updatedText,
							},
						];
					} else {
						// Create new message
						return [
							...prev,
							{
								id: data.Value?.Id || `msg-${Date.now()}`,
								text: messageText,
								timestamp: new Date(),
								isStreaming: true,
								role: 'assistant',
							},
						];
					}
				});

				// Update query cache
				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({ id: params.convoId }),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							// LLM tokens include proper spacing - just concatenate directly
							const updatedText = isDelta ? lastAssistantMessage.text + messageText : messageText;
							return [...messages, { ...lastAssistantMessage, text: updatedText }];
						}
						return [...(messages || []), { id: data.Value?.Id, text: messageText, role: Api.AIChatRole.Assistant }];
					}
				);
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPrompt', error as Api.IOperationResultNoValue);
		},
		onSuccess: () => {
			// Mark last message as complete
			setCoachingMessages(prev => {
				if (prev.length === 0) return prev;
				const lastMessage = prev[prev.length - 1];
				return [...prev.slice(0, -1), { ...lastMessage, isStreaming: false }];
			});
		},
	});

	// Send transcript batch immediately - used by "Get Suggestion" button
	const sendTranscriptBatchImmediately = React.useCallback(
		(entries: TranscriptEntry[]) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId || entries.length === 0) {
				return;
			}

			// Clear any pending entries to avoid duplicates
			setPendingTranscriptEntries([]);

			const formattedTranscript = entries
				.map(entry => `[${entry.timestamp.toLocaleTimeString()}] ${entry.speaker}: ${entry.message}`)
				.join('\n');

			// Wire text leads with the live-coaching header so the BE agent stays tactical; the panel
			// bubble omits it because the rep shouldn't read the agent's instructions back.
			const transcriptText = `New transcript:\n${formattedTranscript}`;
			const wireText = `${LIVE_SUGGESTION_PROMPT_HEADER}\n\n${transcriptText}`;

			// Add user message showing what was sent
			const transcriptMessage: AICoachingMessage = {
				id: `transcript-${Date.now()}`,
				text: transcriptText,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'transcript',
			};

			setCoachingMessages(prev => [...prev, transcriptMessage]);

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: wireText,
							id: null,
						},
					],
				} as Api.AIMessageRequest,
			});
		},
		[aiPromptMutation]
	);

	// Send single transcript entry - accumulates entries until manually sent
	const sendTranscriptEntry = React.useCallback(
		(entry: TranscriptEntry) => {
			if (!conversationIdRef.current) {
				return;
			}
			setPendingTranscriptEntries(prev => [...prev, entry]);
		},
		[] // No dependencies - always use latest ref value
	);

	// Flush pending entries and return them (for manual "Get Suggestion")
	const flushPendingEntries = React.useCallback((): TranscriptEntry[] => {
		const entries = [...pendingTranscriptEntries];
		setPendingTranscriptEntries([]);
		return entries;
	}, [pendingTranscriptEntries]);

	// Create conversation
	const createConversation = React.useCallback(
		async (data?: TysonCoachingContextData) => {
			if (aiAssistantCreateConversationMutation.isPending) return;

			// Use provided data or fall back to contextData
			const ctx = data || contextData;

			// Validate context data
			if (!ctx) {
				throw new Error('Cannot create conversation: contextData is null');
			}

			if (!ctx.companyId) {
				throw new Error('Cannot create conversation: companyId is required');
			}

			if (!ctx.contactId) {
				throw new Error('Cannot create conversation: contactId is required');
			}

			// Create conversation
			await aiAssistantCreateConversationMutation.mutateAsync({
				assistantName: TYSON_ASSISTANT_NAME,
				phoneCallId: ctx.phoneCallId,
				companyId: ctx.companyId,
				leadServedId: ctx.leadServedId,
				agentTemplateId: ctx.agentTemplateId,
			});
		},
		[contextData, aiAssistantCreateConversationMutation]
	);

	// Send end-of-call summary request
	const sendEndOfCallSummary = React.useCallback(() => {
		const currentConversationId = conversationIdRef.current;
		if (!currentConversationId) {
			return;
		}

		aiPromptMutation.mutate({
			assistantName: TYSON_ASSISTANT_NAME,
			convoId: currentConversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: TYSON_END_OF_CALL_SUMMARY_PROMPT,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
		});
	}, [aiPromptMutation]);

	// Send end-of-call summary with transcript included in one request
	const sendEndOfCallSummaryWithTranscript = React.useCallback(
		(formattedTranscript: string) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId) {
				return;
			}

			// Summary ask leads, transcript follows as material — leading with the transcript hid the
			// header the BE agent gates summaries on, so it had to guess whether the call had ended.
			const transcriptText = `New transcript:\n${formattedTranscript}`;
			const wireText = `${TYSON_END_OF_CALL_SUMMARY_PROMPT}\n\n---\n\nTranscript:\n${formattedTranscript}`;

			const transcriptMessage: AICoachingMessage = {
				id: `transcript-${Date.now()}`,
				text: transcriptText,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'transcript',
			};
			setCoachingMessages(prev => [...prev, transcriptMessage]);

			// Send transcript + summary prompt in a single user message
			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: wireText,
							id: null,
						},
					],
				} as Api.AIMessageRequest,
			});
		},
		[aiPromptMutation]
	);

	// Send a direct question from the user (not from call transcript)
	const sendDirectQuestion = React.useCallback(
		(question: string) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId || !question.trim()) return;

			// Add user message to conversation
			const userMessage: AICoachingMessage = {
				id: `question-${Date.now()}`,
				text: question,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'question',
			};
			setCoachingMessages(prev => [...prev, userMessage]);

			// Send with wrapper context
			const wrappedQuestion = `The sales rep is asking you a direct question (not from the call transcript):\n${question}`;

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: wrappedQuestion,
							id: null,
						},
					],
				} as Api.AIMessageRequest,
			});
		},
		[aiPromptMutation]
	);

	// Fire the structured "next contact started" action — the BE handler owns
	// the prompt text it composes for the agent. The UI bubble is a short
	// label, not the wire payload.
	const sendNextContactStartedAction = React.useCallback(
		(contactName: string) => {
			const currentConversationId = conversationIdRef.current;
			const trimmed = contactName.trim();
			if (!currentConversationId || !trimmed) return;

			const bubble: AICoachingMessage = {
				id: `system-${Date.now()}`,
				text: `Calling the next contact: ${trimmed}`,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'system',
			};
			setCoachingMessages(prev => [...prev, bubble]);

			const action = {
				_type: 'TysonNextContactStartedAction',
				contactName: trimmed,
			} as Api.TysonNextContactStartedAction;
			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				// `_type: 'AIActionRequest'` is REQUIRED — the BE deserializes
				// `AIChatRequest` polymorphically and falls back to
				// `AIMessageRequest` without it, then 400s with "Message request
				// must include messages".
				chatRequest: { _type: 'AIActionRequest', action } as Api.AIActionRequest,
			});
		},
		[aiPromptMutation]
	);

	// Fire the structured "practice call started" action — same pattern as
	// next-contact above, but for the practice-call kickoff bubble.
	const sendPracticeCallStartedAction = React.useCallback(
		(contactName: string) => {
			const currentConversationId = conversationIdRef.current;
			const trimmed = contactName.trim();
			if (!currentConversationId || !trimmed) return;

			const bubble: AICoachingMessage = {
				id: `system-${Date.now()}`,
				text: `Starting a new practice call with ${trimmed}`,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'system',
			};
			setCoachingMessages(prev => [...prev, bubble]);

			const action = {
				_type: 'TysonPracticeCallStartedAction',
				contactName: trimmed,
			} as Api.TysonPracticeCallStartedAction;
			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				// `_type: 'AIActionRequest'` is REQUIRED — see note in
				// `sendNextContactStartedAction`. Without it the BE falls back to
				// `AIMessageRequest` and 400s with "Message request must include
				// messages".
				chatRequest: { _type: 'AIActionRequest', action } as Api.AIActionRequest,
			});
		},
		[aiPromptMutation]
	);

	// Enrich the creation context of an existing conversation (e.g., add phoneCallId after Twilio call starts)
	const enrichCreationContext = React.useCallback(
		(params: { phoneCallId?: string; leadServedId?: string; source?: string }) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId) return;

			// Only call the API if there's at least one field to enrich
			if (!params.phoneCallId && !params.leadServedId && !params.source) return;

			enrichCreationContextMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				conversationId: currentConversationId,
				...params,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	// Reset coaching state
	const resetCoaching = React.useCallback(() => {
		setConversationId(null);
		conversationIdRef.current = null;
		setCoachingMessages([]);
		setPendingTranscriptEntries([]);
	}, []);

	return {
		coachingMessages,
		conversationId,
		isCreatingConversation: aiAssistantCreateConversationMutation.isPending,
		isAIResponding: aiPromptMutation.isStreaming || aiPromptMutation.isLoading,
		createConversation,
		enrichCreationContext,
		sendTranscriptEntry,
		sendTranscriptBatchImmediately,
		flushPendingEntries,
		sendEndOfCallSummary,
		sendEndOfCallSummaryWithTranscript,
		sendDirectQuestion,
		sendNextContactStartedAction,
		sendPracticeCallStartedAction,
		resetCoaching,
	};
};
